export interface BillingTransform {
  success: boolean;
  data: {
    file: string;
    errors: string;
  };
}

export interface BillingTransformAccess {
  hasAccess: boolean;
}

export interface IBillingClinic {
  target: {
    value: string;
  };
}

export enum ExtensionType {
  zip = 'zip',
  csv = 'csv',
}

export enum ClaimStatus {
  Pending = 'pending',
  Uploaded = 'uploaded',
  UploadedWithErrors = 'uploaded-with-errors',
  Processed = 'processed',
}

export enum ClaimStage {
  CreationInProgress = 'CREATION_IN_PROGRESS',
  Created = 'CREATED',
  ExportInProgress = 'EXPORT_IN_PROGRESS',
  Exported = 'EXPORTED',
  Discarded = 'DISCARDED',
}

export enum ClaimType {
  RPM = 'rpm',
  Appointment = 'appointment',
  Unknown = 'unknown',
}

export enum EhrName {
  Drchrono = 'drchrono',
}

export enum ExportedClaimStatus {
  EXPORTED = 'EXPORTED',
  EXPORTED_WITH_WARNINGS = 'EXPORTED_WITH_WARNINGS',
  NOT_EXPORTED = 'NOT_EXPORTED',
}

// Unified RPM claim status enum (includes both created and exported statuses)
export enum RPMClaimStatus {
  // Created statuses (claims in CREATED process stage)
  CREATED = 'CREATED',
  CREATED_WITH_WARNINGS = 'CREATED_WITH_WARNINGS',
  CREATED_WITH_ERRORS = 'CREATED_WITH_ERRORS',
  // Exported statuses (claims in EXPORTED process stage)
  EXPORTED = 'EXPORTED',
  EXPORTED_WITH_WARNINGS = 'EXPORTED_WITH_WARNINGS',
  NOT_EXPORTED = 'NOT_EXPORTED',
}

export enum NoteStatus {
  HAS_NOTE = 'HAS_NOTE',
  NO_NOTE = 'NO_NOTE',
  PDF_AVAILABLE = 'PDF_AVAILABLE',
}

export interface EHRSyncPayload {
  fromDate?: string;
  untilDate?: string;
  clinicIds?: number[];
  forceEhrFetching?: boolean;
  enableSlackMessages?: boolean;
}

export interface EHRClaimSync {
  fromDate: string;
  untilDate: string;
  clinicIds?: string;
  providerIds?: string;
  patientIds?: string;
  statuses?: string;
  sources?: string;
  providerName?: string;
  patientName?: string;
  page?: number;
  perPage?: number;
}

export interface PaginationInfo {
  totalCount: number;
  currentPage: number;
  perPage: number;
  totalPages: number;
}

export enum EHRClaimSyncStatus {
  Completed = 'completed',
  CompletedWithErrors = 'completed-with-errors',
  InProgress = 'in-progress',
  Pending = 'pending',
  Stopped = 'stopped',
}

export interface EHRClaimSyncRequest {
  id: number;
  uuid: string;
  clinicId: number;
  fromDate: string;
  untilDate: string;
  status: EHRClaimSyncStatus;
  forceEhrFetching: boolean;
  enableSlackMessages: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  uploaded: number;
  uploadedWithErrors: number;
  requested: number;
}

export interface EHRClaimSyncRequestPayload {
  clinicIds?: string;
  statuses?: string;
  page?: number;
  perPage?: number;
}

export interface EHRClaimSyncRequestResults {
  success: boolean;
  pagination?: { currentPage: number; perPage: number; totalCount: number; totalPages: number };
  claimSyncs: EHRClaimSyncRequest[];
}

export interface AperoClaimSyncResult {
  success: boolean;
  id: number;
  status: string;
}

export interface AperoAsyncClinicResult {
  success: boolean;
  message?: string;
  data?: {
    clinicId: number;
  };
  requestId: number;
}

export interface AperoSyncClinicsResponse {
  success: boolean;
  message?: string;
  clinics: AperoAsyncClinicResult[];
}

export interface DrchronoAppointment {
  id: number;
  patient: number;
}

export interface Scheduling {
  id: number;
}

export interface DrchronoCompleteAppointment {
  source: ClaimType;
  appointment: DrchronoAppointment;
}

export type EhrAppointment = DrchronoCompleteAppointment;

export interface Invoice {
  id: number;
}

export interface CoreLiteUser {
  id: number;
  firstName: string;
  lastName: string;
}

export type AperoInvoiceData = {
  submittedAt: string;
  withErrors: boolean;
  invoice: Invoice;
};

export enum EhrBillableLogEntryType {
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
}
export interface EhrClaimLogEntry {
  id: number;
  type: EhrBillableLogEntryType;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface EhrClaimOverrides {
  aperoProviderId?: string;
  aperoPatientId?: string;
  aperoCustomerId?: string;
}

export interface EhrClaim {
  id: number;
  uuid: string;
  clinicId: number;
  serviceDate: string;
  status: ClaimStatus;
  source: ClaimType;
  ehrName: EhrName;
  ehrAppointmentData: EhrAppointment | null;
  coreAppointmentData: Scheduling | null;
  coreProvider: CoreLiteUser | null;
  corePatient: CoreLiteUser | null;
  aperoInvoiceData: AperoInvoiceData | null;
  ehrClaimLogs: EhrClaimLogEntry[];
  overrides: EhrClaimOverrides | null;
}

export interface EhrClaimsResponse {
  success: boolean;
  message?: string;
  claims: EhrClaim[];
  pagination?: PaginationInfo;
}

export interface EhrClaimsMetricsParams {
  fromDate: string;
  untilDate: string;
  clinicIds?: string;
  sources?: string;
  statuses?: string;
}

export interface EhrClaimsMetricsResponse {
  success: boolean;
  message?: string;
  metrics: {
    total: number;
    byStatus: Record<string, number>;
    bySource: Record<string, number>;
  };
}

export interface EhrClaimsErrorSummaryParams {
  fromDate?: string;
  untilDate?: string;
  clinicIds?: string;
}

export interface ErrorSummaryClaimInfo {
  id: number;
  serviceDate: string;
}

export interface ErrorSummaryItem {
  description: string;
  category: 'provider' | 'error';
  count: number;
  claims: ErrorSummaryClaimInfo[];
}

export interface EhrClaimsErrorSummaryResponse {
  success: boolean;
  message?: string;
  errors: ErrorSummaryItem[];
}

export interface AgingBucket {
  label: string;
  minDays: number;
  maxDays: number | null;
  count: number;
}

export interface EhrClaimsAgingParams {
  clinicIds: string;
}

export interface EhrClaimsAgingResponse {
  success: boolean;
  message?: string;
  total: number;
  buckets: AgingBucket[];
}

export interface EhrClaimResponse {
  success: boolean;
  message?: string;
  claim: EhrClaim | null;
}

export interface EhrClaimResyncPayload {
  forceEhrFetching?: boolean;
  enableSlackMessages?: boolean;
}

export interface RpmPatientSelectionFilters {
  clinicIds?: number[];
  icd10Codes?: string[];
  rpmStatuses?: string[];
  insuranceCompanyIds?: string[];
  cptCodes?: string[];
  optOutStatus?: string[];
  memberIds?: string[];
  page?: number;
  limit?: number;
}

export interface RpmCalculationFilters {
  clinicIds?: number[];
  insuranceCompanyIds?: string[];
  dateOfService?: string;
  placeOfService?: string[];
  cptCodes?: string[];
  memberUuids?: string[];
  claimStatus?: string[];
  processId?: number;
  page?: number;
  limit?: number;
}

export interface RpmExportedFilters {
  clinicIds?: number[];
  insuranceCompanyIds?: string[];
  dateOfService?: string;
  cptCodes?: string[];
  memberUuids?: string[];
  claimStatus?: string[];
  processId?: number;
  page?: number;
  limit?: number;
}

export interface ExportedClaimsFilters extends RpmExportedFilters {
  invoiceCreationDateFrom?: string;
  invoiceCreationDateTo?: string;
  processIds?: number[];
  statuses?: string[];
  exportedByUserIds?: string[];
  noteStatus?: NoteStatus[];
  processId?: number; // Make processId optional for cross-process search
}

// Unified RPM Claims Filters (supports both created and exported claims)
export interface RPMClaimsFilters extends RpmExportedFilters {
  claimDateFrom?: string; // More generic than invoiceCreationDateFrom
  claimDateTo?: string; // More generic than invoiceCreationDateTo
  processIds?: number[];
  statuses?: RPMClaimStatus[] | string[]; // All 6 claim statuses
  exportedByUserIds?: string[]; // Only applies to exported claims
  noteStatus?: NoteStatus[];
  processId?: number; // Make processId optional for cross-process search
}

// Unified GET /claims endpoint types (NEW)
export type ClaimSortField = 'id' | 'dateOfService' | 'createdAt' | 'processId' | 'clinicId' | 'insuranceCompanyId';
export type SortOrder = 'asc' | 'desc';

export interface GetAllClaimsParams {
  page?: number;
  limit?: number;
  dateOfService?: string; // ISO date string
  createdAtFrom?: string; // ISO date string
  createdAtTo?: string; // ISO date string
  clinicIds?: number[];
  insuranceCompanyIds?: string[];
  cptCodes?: string[];
  memberUuids?: string[];
  processId?: number;
  processIds?: number[];
  statuses?: RPMClaimStatus[] | string[];
  sortBy?: ClaimSortField;
  sortOrder?: SortOrder;
}

// Provider with Apero ID for override selection
export interface ProviderWithAperoId {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  calendar_id: string | null;
  timezone: string | null;
  apero_provider_id: string | null;
}

export interface ProvidersResponse {
  providers: ProviderWithAperoId[];
  pagination?: {
    current_page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}
